import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, LifeBuoy, Smartphone, Mail, Handshake, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_FOOTER_LINKS = [
  { title: 'Pourquoi SENDBOX', url: '#' },
  { title: 'FAQ', url: '#' },
  { title: 'Sécurité', url: '#' },
  { title: 'Mentions légales', url: '#' }
];

// Smart link component that reloads page if already on it
const SmartLink = ({ to, children, className, ...props }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCurrentPage = location.pathname === to;
  
  const handleClick = (e) => {
    if (isCurrentPage) {
      e.preventDefault();
      // Force navigation with a timestamp to trigger re-render
      navigate(`${to}?t=${Date.now()}`, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  return (
    <Link 
      to={to} 
      onClick={handleClick} 
      className={`${className} ${isCurrentPage ? 'text-cyan-400' : ''}`}
      {...props}
    >
      {children}
    </Link>
  );
};

const DynamicFooter = ({ fixed = false, className = '' }) => {
  const [footerPages, setFooterPages] = useState(DEFAULT_FOOTER_LINKS);

  useEffect(() => {
    loadFooterPages();
  }, []);

  const loadFooterPages = async () => {
    try {
      const res = await axios.get(`${API}/footer/pages`);
      const pages = res.data.pages || [];
      if (pages.length > 0) setFooterPages(pages);
    } catch (error) {
      console.error('Error loading footer pages:', error);
    }
  };

  const footerClasses = fixed 
    ? `fixed bottom-0 left-0 right-0 px-6 py-4 bg-blue-950/30 backdrop-blur-xl border-t border-blue-500/20 z-40 ${className}`
    : `border-t border-blue-500/10 py-8 mt-12 ${className}`;

  if (fixed) {
    return (
      <footer className={footerClasses}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <nav className="flex flex-wrap items-center gap-6 text-sm">
              {footerPages.filter(p => p.title.toLowerCase() !== 'contact').map((page, index) => (
                page.url.startsWith('/') ? (
                  <Link key={index} to={page.url} className="text-blue-300/70 hover:text-blue-200 transition-colors duration-300">
                    {page.title}
                  </Link>
                ) : (
                  <a key={index} href={page.url} className="text-blue-300/70 hover:text-blue-200 transition-colors duration-300">
                    {page.title}
                  </a>
                )
              ))}
            </nav>
            <div className="flex items-center gap-4">
              <SmartLink to="/contact" className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-300 transition-colors text-sm" data-testid="footer-contact-link">
                <Mail className="w-3.5 h-3.5" /> Contact
              </SmartLink>
              <SmartLink to="/app-mobile" className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-300 transition-colors text-sm" data-testid="footer-mobile-link">
                <Smartphone className="w-3.5 h-3.5" /> App Mobile
              </SmartLink>
              <SmartLink to="/support" className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-300 transition-colors text-sm" data-testid="footer-support-link">
                <LifeBuoy className="w-3.5 h-3.5" /> Support
              </SmartLink>
              <SmartLink to="/partenaires" className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-300 transition-colors text-sm" data-testid="footer-partners-link-fixed">
                <Handshake className="w-3.5 h-3.5" /> Partenaires
              </SmartLink>
              <SmartLink to="/publicite" className="inline-flex items-center gap-1.5 text-blue-300/70 hover:text-blue-300 transition-colors text-sm" data-testid="footer-advertising-link">
                <Megaphone className="w-3.5 h-3.5" /> Publicité
              </SmartLink>
              <Button variant="outline" size="sm" className="bg-blue-500/10 border-blue-400/30 text-blue-200 hover:bg-blue-500/20 hover:text-white transition-all duration-300">
                <Globe className="w-4 h-4 mr-2" />
                Français
              </Button>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={footerClasses}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <nav className="flex flex-wrap items-center gap-6 text-sm">
            {footerPages.filter(p => p.title.toLowerCase() !== 'contact').map((page, index) => (
              page.url.startsWith('/') ? (
                <SmartLink key={index} to={page.url} className="text-blue-300/50 hover:text-blue-300 transition-colors">
                  {page.title}
                </SmartLink>
              ) : (
                <a key={index} href={page.url} className="text-blue-300/50 hover:text-blue-300 transition-colors">
                  {page.title}
                </a>
              )
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <SmartLink to="/contact" className="inline-flex items-center gap-1.5 text-blue-300/50 hover:text-blue-300 transition-colors text-sm" data-testid="footer-contact-link-alt">
              <Mail className="w-3.5 h-3.5" /> Contact
            </SmartLink>
            <SmartLink to="/app-mobile" className="inline-flex items-center gap-1.5 text-blue-300/50 hover:text-blue-300 transition-colors text-sm" data-testid="footer-mobile-link-alt">
              <Smartphone className="w-3.5 h-3.5" /> App Mobile
            </SmartLink>
            <SmartLink to="/support" className="inline-flex items-center gap-1.5 text-blue-300/50 hover:text-blue-300 transition-colors text-sm" data-testid="footer-support-link-alt">
              <LifeBuoy className="w-3.5 h-3.5" /> Support
            </SmartLink>
            <SmartLink to="/partenaires" className="inline-flex items-center gap-1.5 text-blue-300/50 hover:text-blue-300 transition-colors text-sm" data-testid="footer-partners-link">
              <Handshake className="w-3.5 h-3.5" /> Partenaires
            </SmartLink>
            <SmartLink to="/publicite" className="inline-flex items-center gap-1.5 text-blue-300/50 hover:text-blue-300 transition-colors text-sm" data-testid="footer-advertising-link-alt">
              <Megaphone className="w-3.5 h-3.5" /> Publicité
            </SmartLink>
            <Button variant="outline" size="sm" className="bg-blue-500/10 border-blue-400/30 text-blue-200 hover:bg-blue-500/20 hover:text-white transition-all duration-300">
              <Globe className="w-4 h-4 mr-2" />
              Français
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default DynamicFooter;
